import { Seed } from '@/types';
import { PRIVACY_REDACTION } from '@/utils/statisticValidation.util';

/**
 * Utility functions for formatting and processing seed data in the graph visualization
 */

/**
 * Extract the root domain from a full domain name
 * e.g., "mail.example.com" -> "example.com"
 */
export const extractRootDomain = (domain: string): string => {
  if (!domain) return '';

  // Remove protocol if present
  const cleanDomain = domain.replace(/^https?:\/\//, '').toLowerCase();

  // Remove path if present
  const domainOnly = cleanDomain.split('/')[0];

  // Split by dots
  const parts = domainOnly.split('.');

  // Handle edge cases
  if (parts.length <= 2) return domainOnly;

  // Check for known multi-part TLDs (co.uk, com.au, etc.)
  const multiPartTLDs = [
    'co.uk',
    'com.au',
    'co.nz',
    'co.za',
    'com.br',
    'co.in',
  ];
  const lastTwo = parts.slice(-2).join('.');

  if (multiPartTLDs.includes(lastTwo)) {
    return parts.slice(-3).join('.');
  }

  // Return last two parts for standard domains
  return parts.slice(-2).join('.');
};

/**
 * Extract TLD from domain
 * e.g., "example.com" -> "com", "example.co.uk" -> "co.uk"
 */
export const extractTLD = (domain: string): string => {
  if (!domain) return '';

  const cleanDomain = domain.replace(/^https?:\/\//, '').toLowerCase();
  const domainOnly = cleanDomain.split('/')[0];
  const parts = domainOnly.split('.');

  if (parts.length < 2) return '';

  // Check for known multi-part TLDs
  const multiPartTLDs = [
    'co.uk',
    'com.au',
    'co.nz',
    'co.za',
    'com.br',
    'co.in',
  ];
  const lastTwo = parts.slice(-2).join('.');

  if (multiPartTLDs.includes(lastTwo)) {
    return lastTwo;
  }

  return parts[parts.length - 1];
};

/**
 * Format expiration date into human-readable status
 */
export const formatExpirationStatus = (
  expiration: string | Date | undefined
): string => {
  if (!expiration) return 'Unknown';

  const expirationDate =
    typeof expiration === 'string' ? new Date(expiration) : expiration;
  const now = new Date();
  const daysUntilExpiration = Math.floor(
    (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysUntilExpiration < 0) {
    return `Expired ${Math.abs(daysUntilExpiration)} days ago`;
  } else if (daysUntilExpiration === 0) {
    return 'Expires today';
  } else if (daysUntilExpiration <= 30) {
    return `Expires in ${daysUntilExpiration} days`;
  } else if (daysUntilExpiration <= 90) {
    return `Expires in ${Math.floor(daysUntilExpiration / 30)} months`;
  } else if (daysUntilExpiration <= 365) {
    return `Expires this year`;
  } else {
    const years = Math.floor(daysUntilExpiration / 365);
    return `Expires in ${years} year${years > 1 ? 's' : ''}`;
  }
};

/**
 * Get expiration year from date
 */
export const getExpirationYear = (
  expiration: string | Date | undefined
): string => {
  if (!expiration) return 'Unknown';

  const expirationDate =
    typeof expiration === 'string' ? new Date(expiration) : expiration;
  return expirationDate.getFullYear().toString();
};

/**
 * Calculate domain age in human-readable format
 */
export const calculateDomainAge = (
  created: string | Date | undefined
): string => {
  if (!created) return 'Unknown';

  const createdDate = typeof created === 'string' ? new Date(created) : created;
  const now = new Date();
  const ageInDays = Math.floor(
    (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (ageInDays < 30) {
    return `${ageInDays} days`;
  } else if (ageInDays < 365) {
    const months = Math.floor(ageInDays / 30);
    return `${months} month${months > 1 ? 's' : ''}`;
  } else {
    const years = Math.floor(ageInDays / 365);
    const remainingMonths = Math.floor((ageInDays % 365) / 30);
    if (remainingMonths > 0) {
      return `${years} year${years > 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
    }
    return `${years} year${years > 1 ? 's' : ''}`;
  }
};

/**
 * Standardize registrar names for consistent clustering
 */
export const standardizeRegistrar = (registrar: string | undefined): string => {
  if (!registrar) return 'Unknown';

  // Common registrar name variations to standardize
  const registrarMap: Record<string, string> = {
    godaddy: 'GoDaddy',
    'godaddy.com': 'GoDaddy',
    'godaddy.com, llc': 'GoDaddy',
    namecheap: 'Namecheap',
    'namecheap, inc.': 'Namecheap',
    'namecheap inc': 'Namecheap',
    'network solutions': 'Network Solutions',
    'network solutions, llc': 'Network Solutions',
    cloudflare: 'Cloudflare',
    'cloudflare, inc.': 'Cloudflare',
    amazon: 'Amazon',
    'amazon registrar': 'Amazon',
    'amazon registrar, inc.': 'Amazon',
    google: 'Google',
    'google domains': 'Google',
    'google llc': 'Google',
  };

  const lowerRegistrar = registrar.toLowerCase().trim();

  // Check if we have a mapping for this registrar
  for (const [key, value] of Object.entries(registrarMap)) {
    if (lowerRegistrar.includes(key)) {
      return value;
    }
  }

  // If no mapping, return the original with proper casing
  return registrar
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format seed status
 */
export const formatSeedStatus = (status: string | undefined): string => {
  if (!status) return 'Unknown';

  const statusMap: Record<string, string> = {
    A: 'Active',
    I: 'Inactive',
    E: 'Expired',
    P: 'Pending',
    D: 'Deleted',
    S: 'Suspended',
  };

  return statusMap[status] || status;
};

/**
 * Extract country from registrant information
 */
export const extractRegistrantCountry = (
  registrant: string | undefined
): string => {
  if (!registrant) return 'Unknown';

  // Common country patterns in registrant strings
  const countryPatterns = [
    /\b(USA?|United States)\b/i,
    /\b(UK|United Kingdom|GB|Great Britain)\b/i,
    /\b(CA|Canada)\b/i,
    /\b(AU|Australia)\b/i,
    /\b(DE|Germany|Deutschland)\b/i,
    /\b(FR|France)\b/i,
    /\b(NL|Netherlands)\b/i,
    /\b(JP|Japan)\b/i,
    /\b(CN|China)\b/i,
    /\b(IN|India)\b/i,
  ];

  const countryNames: Record<number, string> = {
    0: 'United States',
    1: 'United Kingdom',
    2: 'Canada',
    3: 'Australia',
    4: 'Germany',
    5: 'France',
    6: 'Netherlands',
    7: 'Japan',
    8: 'China',
    9: 'India',
  };

  for (let i = 0; i < countryPatterns.length; i++) {
    if (countryPatterns[i].test(registrant)) {
      return countryNames[i];
    }
  }

  return 'Unknown';
};

/**
 * Pretty format seed value based on field type
 */
export const prettifySeedValue = (
  field: string,
  value: string | undefined
): string => {
  if (!value) return 'Unknown';

  switch (field) {
    case 'identifier':
      return value.toLowerCase();

    case 'rootDomain':
      return extractRootDomain(value);

    case 'tld':
      return extractTLD(value).toUpperCase();

    case 'registrar':
      return standardizeRegistrar(value);

    case 'registrant':
      // Truncate long registrant names for display
      return value.length > 50 ? value.substring(0, 47) + '...' : value;

    case 'status':
      return formatSeedStatus(value);

    case 'expiration':
    case 'expirationStatus':
      return formatExpirationStatus(value);

    case 'expirationYear':
      return getExpirationYear(value);

    case 'created':
    case 'domainAge':
      return calculateDomainAge(value);

    case 'registrantCountry':
    case 'country':
      return extractRegistrantCountry(value);

    default:
      return value;
  }
};

/**
 * Validate seed data for clustering
 */
export const isValidSeedValue = (value: string | undefined | null): boolean => {
  if (!value) return false;

  const trimmed = value.trim().toLowerCase();

  // Check for invalid values
  const invalidValues = [
    '',
    'unknown',
    'undefined',
    'null',
    'none',
    'n/a',
    'not available',
    'redacted',
    'private',
    'protected',
    PRIVACY_REDACTION.toLowerCase(),
  ];

  return !invalidValues.includes(trimmed);
};

/**
 * Get seed health status based on available fields
 */
export const getSeedHealthStatus = (
  seed: Seed
): {
  status: 'healthy' | 'warning' | 'critical';
  message: string;
} => {
  // Check status
  if (seed.status && seed.status !== 'A') {
    return { status: 'warning', message: 'Domain not active' };
  }

  // Check if recently visited (within last 30 days)
  if (seed.visited) {
    const visitedDate = new Date(seed.visited);
    const now = new Date();
    const daysSinceVisited = Math.floor(
      (now.getTime() - visitedDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceVisited > 90) {
      return { status: 'warning', message: 'Not recently scanned' };
    }
  }

  return { status: 'healthy', message: 'Domain healthy' };
};

/**
 * Group seeds by root domain
 */
export const groupSeedsByRootDomain = (seeds: Seed[]): Map<string, Seed[]> => {
  const groups = new Map<string, Seed[]>();

  seeds.forEach(seed => {
    const rootDomain = extractRootDomain(seed.identifier);
    if (!groups.has(rootDomain)) {
      groups.set(rootDomain, []);
    }
    groups.get(rootDomain)!.push(seed);
  });

  return groups;
};

/**
 * Calculate seed discovery efficiency
 */
export const calculateSeedEfficiency = (
  seed: Seed,
  discoveredAssets: number,
  scanCount: number = 1
): number => {
  if (scanCount === 0) return 0;
  return Math.round((discoveredAssets / scanCount) * 100) / 100;
};
